import { useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import {
  AppBar, Avatar, Box, Chip, Collapse, Divider, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Menu, MenuItem, Toolbar, Tooltip, Typography, Badge, ListSubheader,
} from '@mui/material'
import * as Icons from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { NAVIGATION, findNavItem, type NavGroup } from './navigation'
import { useAuth } from '../auth/AuthContext'
import { getUnreadNotificationCount } from '../api/notificari'

const DRAWER_WIDTH = 244

function GroupIcon({ name }: { name: string }) {
  const Cmp = (Icons as unknown as Record<string, React.ElementType>)[name] ?? Icons.Circle
  return <Cmp fontSize="small" />
}

export default function AppShell({ mode, onToggleMode }: { mode: 'light' | 'dark'; onToggleMode: () => void }) {
  const { t, i18n } = useTranslation()
  const { user, logout, logoutAll, switchWorkPoint, can } = useAuth()
  const location = useLocation()

  const current = findNavItem(location.pathname)
  const [open, setOpen] = useState<Record<string, boolean>>(
    () => (current ? { [current.group.key]: true } : { acasa: true }),
  )
  const [userMenu, setUserMenu] = useState<null | HTMLElement>(null)
  const [wpMenu, setWpMenu] = useState<null | HTMLElement>(null)

  const canSeeNotifications = can('administrare.notificarile-mele.vizualizare')
  const { data: unreadCount } = useQuery({
    queryKey: ['notificari', 'numar-necitite'], queryFn: getUnreadNotificationCount,
    enabled: canSeeNotifications, refetchInterval: 60_000,
  })

  const toggleLanguage = () => void i18n.changeLanguage(i18n.language === 'ro' ? 'en' : 'ro')

  // Un grup apare doar dacă utilizatorul are cel puțin un ecran vizibil în el.
  const visibleGroups = NAVIGATION.map((g) => ({
    ...g, items: g.items.filter((i) => can(i.permission)),
  })).filter((g) => g.items.length > 0)

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{ zIndex: (th) => th.zIndex.drawer + 1, bgcolor: '#12211F', borderBottom: 1, borderColor: '#22332F' }}
      >
        <Toolbar variant="dense" sx={{ gap: 1.5, minHeight: 52 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
            <Box sx={{
              width: 24, height: 24, borderRadius: 1.2, bgcolor: 'primary.main', color: '#fff',
              display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 12,
            }}>C</Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
              {t('common.appName')}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Comutator de punct de lucru — filtrează implicit tot ce urmează (FR-ADM-003a) */}
          <Chip
            size="small"
            label={user?.workPointName ?? '—'}
            onClick={(e) => setWpMenu(e.currentTarget)}
            icon={<Icons.Place sx={{ fontSize: 15 }} />}
            sx={{ bgcolor: 'rgba(255,255,255,.08)', color: '#D6E2DF', '& .MuiChip-icon': { color: '#7E938E' } }}
          />
          <Menu anchorEl={wpMenu} open={!!wpMenu} onClose={() => setWpMenu(null)}>
            {user?.workPoints.map((wp) => (
              <MenuItem
                key={wp.id}
                selected={wp.id === user.workPointId}
                onClick={() => { void switchWorkPoint(wp.id); setWpMenu(null) }}
              >
                {wp.name}
              </MenuItem>
            ))}
          </Menu>

          <Tooltip title={t('common.language')}>
            <Chip size="small" label={i18n.language.toUpperCase()} onClick={toggleLanguage}
              sx={{ bgcolor: 'rgba(255,255,255,.08)', color: '#D6E2DF', fontWeight: 600 }} />
          </Tooltip>

          <Tooltip title={t('common.theme')}>
            <IconButton size="small" onClick={onToggleMode} sx={{ color: '#D6E2DF' }}>
              {mode === 'dark' ? <Icons.LightMode fontSize="small" /> : <Icons.DarkMode fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title={t('common.approvals')}>
            <IconButton size="small" component={Link} to="/aprobari" sx={{ color: '#D6E2DF' }}>
              <Badge badgeContent={0} color="warning"><Icons.TaskAlt fontSize="small" /></Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title={t('common.notifications')}>
            <IconButton size="small" component={Link} to="/notificarile-mele" sx={{ color: '#D6E2DF' }}>
              <Badge badgeContent={unreadCount ?? 0} color="error"><Icons.NotificationsNone fontSize="small" /></Badge>
            </IconButton>
          </Tooltip>

          <IconButton size="small" onClick={(e) => setUserMenu(e.currentTarget)}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
              {user?.fullName.split(' ').map((p) => p[0]).slice(0, 2).join('')}
            </Avatar>
          </IconButton>
          <Menu anchorEl={userMenu} open={!!userMenu} onClose={() => setUserMenu(null)}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{user?.fullName}</Typography>
              <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
              <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {user?.roles.map((r) => <Chip key={r} size="small" label={r} variant="outlined" />)}
              </Box>
            </Box>
            <Divider />
            <MenuItem component={Link} to="/administrare/sesiuni" onClick={() => setUserMenu(null)}>
              <ListItemIcon><Icons.Devices fontSize="small" /></ListItemIcon>Sesiuni active
            </MenuItem>
            <MenuItem onClick={() => void logout()}>
              <ListItemIcon><Icons.Logout fontSize="small" /></ListItemIcon>{t('common.logout')}
            </MenuItem>
            <MenuItem onClick={() => void logoutAll()}>
              <ListItemIcon><Icons.PhonelinkErase fontSize="small" /></ListItemIcon>{t('common.logoutAll')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH, flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH, boxSizing: 'border-box',
            bgcolor: '#12211F', color: '#D6E2DF', borderRight: '1px solid #22332F',
          },
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 52 }} />
        <Box sx={{ overflowY: 'auto', py: 0.5 }}>
          {visibleGroups.map((group) => (
            <NavGroupBlock
              key={group.key}
              group={group}
              open={open[group.key] ?? false}
              activePath={location.pathname}
              onToggle={() => setOpen((s) => ({ ...s, [group.key]: !s[group.key] }))}
            />
          ))}
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', minWidth: 0 }}>
        <Toolbar variant="dense" sx={{ minHeight: 52 }} />
        <Box sx={{ p: 3 }}><Outlet /></Box>
      </Box>
    </Box>
  )
}

function NavGroupBlock({ group, open, activePath, onToggle }: {
  group: NavGroup; open: boolean; activePath: string; onToggle: () => void
}) {
  const { t } = useTranslation()
  return (
    <List dense disablePadding subheader={<ListSubheader sx={{ display: 'none' }} />}>
      <ListItemButton onClick={onToggle} sx={{ py: 0.75, '&:hover': { bgcolor: '#1D3733' } }}>
        <ListItemIcon sx={{ minWidth: 30, color: 'primary.main' }}><GroupIcon name={group.icon} /></ListItemIcon>
        <ListItemText slotProps={{ primary: { sx: { fontSize: 13, fontWeight: 600 } } }} primary={group.label} />
        {open ? <Icons.ExpandLess sx={{ fontSize: 16, color: '#7E938E' }} />
              : <Icons.ChevronRight sx={{ fontSize: 16, color: '#7E938E' }} />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List dense disablePadding>
          {group.items.map((item) => {
            const active = activePath === item.path
            return (
              <ListItemButton
                key={item.key}
                component={Link}
                to={item.path}
                selected={active}
                sx={{
                  pl: 4.5, py: 0.5, borderLeft: '2px solid transparent',
                  color: active ? '#fff' : '#7E938E',
                  '&.Mui-selected': { bgcolor: '#1D3733', borderLeftColor: 'primary.main' },
                  '&.Mui-selected:hover': { bgcolor: '#1D3733' },
                  '&:hover': { bgcolor: '#1D3733', color: '#D6E2DF' },
                }}
              >
                <ListItemText slotProps={{ primary: { sx: { fontSize: 12.5 } } }} primary={item.label} />
                {item.phase > 1 && (
                  <Chip
                    size="small" variant="outlined"
                    label={item.phase === 2 ? t('common.phase2') : t('common.phase3')}
                    sx={{ height: 16, fontSize: 9, borderColor: '#2C534F', color: '#61A8DC' }}
                  />
                )}
              </ListItemButton>
            )
          })}
        </List>
      </Collapse>
    </List>
  )
}
